import React, { useRef, useState, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Popover,
  Chip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';

const ROW_HEIGHT = 40;
const ROW_GAP = 6;
const LABEL_WIDTH = 160;
const PADDING_TOP = 40;
const PADDING_BOTTOM = 30;
const PADDING_RIGHT = 16;
const AXIS_HEIGHT = 28;
const MIN_CHART_WIDTH = 200;

// Normalise the new Firestore contract doc into the legacy flat shape this
// component was written for. Keeps the bulk of the timeline rendering code
// (1400+ lines of SVG math etc.) untouched. When we modernise the layout
// we can drop this shim.
const normalizeContract = (c) => {
  if (!c) return c;
  if (c.original_filename !== undefined) return c;     // already legacy shape
  return {
    ...c,
    id:                c.contract_id ?? c.id,
    original_filename: c.source?.filename ?? '(unnamed)',
    contract_type:     c.contract_kind,
    start_date:        c.effective_date,
    end_date:          c.termination_date,
    status:            c.processing?.status,
    extraction_summary: c.extraction_summary,
  };
};

const ContractTimeline = ({ contracts, onContractClick }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredContract, setHoveredContract] = useState(null);

  // Sort all contracts: those with valid dates first, then those without
  const allContracts = useMemo(() => {
    const normalized = (contracts || []).map(normalizeContract);
    const withDates = normalized
      .filter((c) => c.start_date && c.end_date && new Date(c.start_date) < new Date(c.end_date))
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const withoutDates = normalized
      .filter((c) => !c.start_date || !c.end_date || new Date(c.start_date) >= new Date(c.end_date));
    return [...withDates, ...withoutDates];
  }, [contracts]);

  // Contracts with valid date ranges (used for time axis calculation)
  const datedContracts = useMemo(() => {
    return allContracts.filter(
      (c) => c.start_date && c.end_date && new Date(c.start_date) < new Date(c.end_date)
    );
  }, [allContracts]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        if (containerWidth > 0) {
          setWidth(containerWidth);
        }
      }
    };

    updateWidth();
    const raf = requestAnimationFrame(updateWidth);
    const timeoutId = setTimeout(updateWidth, 100);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateWidth);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [allContracts]);

  // Calculate time range from dated contracts
  const { minDate, maxDate } = useMemo(() => {
    if (datedContracts.length === 0) return { minDate: null, maxDate: null };
    const dates = datedContracts.flatMap((c) => [
      new Date(c.start_date),
      new Date(c.end_date),
    ]);
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    const range = max - min;
    const pad = range * 0.02 || 86400000 * 30;
    return {
      minDate: new Date(min.getTime() - pad),
      maxDate: new Date(max.getTime() + pad),
    };
  }, [datedContracts]);

  const chartWidth = Math.max(width - LABEL_WIDTH - PADDING_RIGHT, MIN_CHART_WIDTH);
  const svgWidth = LABEL_WIDTH + chartWidth + PADDING_RIGHT;
  const svgHeight =
    PADDING_TOP +
    allContracts.length * (ROW_HEIGHT + ROW_GAP) +
    AXIS_HEIGHT +
    PADDING_BOTTOM;

  // Time to X position
  const timeToX = (date) => {
    if (!minDate || !maxDate) return 0;
    const ratio =
      (new Date(date).getTime() - minDate.getTime()) /
      (maxDate.getTime() - minDate.getTime());
    return LABEL_WIDTH + ratio * chartWidth;
  };

  // Generate tick marks
  const ticks = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const rangeMs = maxDate.getTime() - minDate.getTime();
    const rangeDays = rangeMs / (1000 * 60 * 60 * 24);

    const result = [];
    const current = new Date(minDate);

    if (rangeDays > 365 * 10) {
      current.setMonth(0, 1);
      current.setFullYear(Math.ceil(current.getFullYear() / 5) * 5);
      while (current <= maxDate) {
        result.push(new Date(current));
        current.setFullYear(current.getFullYear() + 5);
      }
    } else if (rangeDays > 365 * 3) {
      current.setMonth(0, 1);
      current.setFullYear(current.getFullYear() + 1);
      while (current <= maxDate) {
        result.push(new Date(current));
        current.setFullYear(current.getFullYear() + 1);
      }
    } else if (rangeDays > 365) {
      current.setDate(1);
      current.setMonth(Math.ceil((current.getMonth() + 1) / 3) * 3);
      while (current <= maxDate) {
        result.push(new Date(current));
        current.setMonth(current.getMonth() + 3);
      }
    } else {
      current.setDate(1);
      current.setMonth(current.getMonth() + 1);
      while (current <= maxDate) {
        result.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
    }
    return result;
  }, [minDate, maxDate]);

  const handleBarHover = (event, contract) => {
    setAnchorEl(event.currentTarget);
    setHoveredContract(contract);
  };

  const handleBarLeave = () => {
    setAnchorEl(null);
    setHoveredContract(null);
  };

  if (allContracts.length === 0) {
    return null;
  }

  const barColor = (type) =>
    type === 'PPA' ? '#D98A2B' : '#3b82f6';
  const barBg = (type) =>
    type === 'PPA' ? 'rgba(217, 138, 43, 0.12)' : 'rgba(59, 130, 246, 0.12)';

  const todayX = timeToX(new Date());

  const formatTickLabel = (date) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    if (date.getMonth() === 0 && date.getDate() === 1) {
      return `${date.getFullYear()}`;
    }
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Defensive: tolerate undefined/null filenames so a single malformed
  // contract doesn't crash the whole timeline. The bug we hit (
  // "Cannot read properties of undefined (reading 'length')") was here.
  const truncateLabel = (str, maxLen = 20) => {
    const s = str ?? '';
    return s.length > maxLen ? s.slice(0, maxLen - 1) + '\u2026' : s;
  };

  const hasDates = (c) =>
    c.start_date && c.end_date && new Date(c.start_date) < new Date(c.end_date);

  return (
    <Paper
      className="animate-in stagger-1"
      sx={{
        mb: 3,
        p: 0,
        bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        overflow: 'visible',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid', borderBottomColor: 'divider',
        }}
      >
        <TimelineIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.01em',
          }}
        >
          Contract Timeline
        </Typography>
        <Chip
          label={`${allContracts.length} contract${allContracts.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            bgcolor: 'background.default',
            color: 'text.secondary',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.68rem',
            fontWeight: 500,
            height: 22,
            border: '1px solid', borderColor: 'divider',
          }}
        />
      </Box>

      {/* Chart */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'visible',
          px: 1,
          py: 1,
        }}
      >
        {width > 0 && (
          <svg
            width={Math.max(svgWidth, width - 16)}
            height={svgHeight}
            style={{ display: 'block' }}
          >
            {/* Grid lines for month ticks */}
            {ticks.map((tick, i) => {
              const x = timeToX(tick);
              return (
                <line
                  key={`grid-${i}`}
                  x1={x}
                  y1={PADDING_TOP - 8}
                  x2={x}
                  y2={svgHeight - PADDING_BOTTOM}
                  stroke="rgba(255, 255, 255, 0.05)"
                  strokeWidth={1}
                />
              );
            })}

            {/* X-axis tick labels */}
            {ticks.map((tick, i) => {
              const x = timeToX(tick);
              return (
                <text
                  key={`tick-${i}`}
                  x={x}
                  y={svgHeight - PADDING_BOTTOM + 16}
                  textAnchor="middle"
                  fill="#a8b0bd"
                  fontSize={10}
                  fontFamily="'DM Sans', sans-serif"
                >
                  {formatTickLabel(tick)}
                </text>
              );
            })}

            {/* Today line */}
            {todayX > LABEL_WIDTH && todayX < svgWidth - PADDING_RIGHT && (
              <>
                <line
                  x1={todayX}
                  y1={PADDING_TOP - 8}
                  x2={todayX}
                  y2={svgHeight - PADDING_BOTTOM}
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  opacity={0.6}
                />
                <text
                  x={todayX}
                  y={PADDING_TOP - 14}
                  textAnchor="middle"
                  fill="#ef4444"
                  fontSize={9}
                  fontWeight={600}
                  fontFamily="'DM Sans', sans-serif"
                >
                  Today
                </text>
              </>
            )}

            {/* Contract rows */}
            {allContracts.map((contract, index) => {
              const y = PADDING_TOP + index * (ROW_HEIGHT + ROW_GAP);
              const color = barColor(contract.contract_type);
              const bg = barBg(contract.contract_type);
              const dated = hasDates(contract);

              return (
                <g key={contract.id}>
                  {/* Row label */}
                  <text
                    x={LABEL_WIDTH - 12}
                    y={y + ROW_HEIGHT / 2 + 4}
                    textAnchor="end"
                    fill="#a8b0bd"
                    fontSize={11}
                    fontFamily="'DM Sans', sans-serif"
                  >
                    {truncateLabel(contract.original_filename)}
                  </text>

                  {/* Background bar (full row) */}
                  <rect
                    x={LABEL_WIDTH}
                    y={y}
                    width={chartWidth}
                    height={ROW_HEIGHT}
                    fill={index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'}
                    rx={4}
                  />

                  {dated ? (
                    <>
                      {/* Contract bar */}
                      <rect
                        x={timeToX(contract.start_date)}
                        y={y + 8}
                        width={Math.max(timeToX(contract.end_date) - timeToX(contract.start_date), 4)}
                        height={ROW_HEIGHT - 16}
                        fill={bg}
                        stroke={color}
                        strokeWidth={1.5}
                        rx={5}
                        style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                        onMouseEnter={(e) => handleBarHover(e, contract)}
                        onMouseLeave={handleBarLeave}
                        onClick={() => onContractClick && onContractClick(contract.id)}
                      />

                      {/* Bar inner fill */}
                      <rect
                        x={timeToX(contract.start_date) + 1}
                        y={y + 9}
                        width={Math.max(timeToX(contract.end_date) - timeToX(contract.start_date) - 2, 2)}
                        height={ROW_HEIGHT - 18}
                        fill={color}
                        rx={4}
                        opacity={0.2}
                        style={{ pointerEvents: 'none' }}
                      />

                      {/* Bar text (if wide enough) */}
                      {(timeToX(contract.end_date) - timeToX(contract.start_date)) > 80 && (
                        <text
                          x={timeToX(contract.start_date) + (timeToX(contract.end_date) - timeToX(contract.start_date)) / 2}
                          y={y + ROW_HEIGHT / 2 + 4}
                          textAnchor="middle"
                          fill={color}
                          fontSize={10}
                          fontWeight={600}
                          fontFamily="'JetBrains Mono', monospace"
                          style={{ pointerEvents: 'none' }}
                        >
                          {contract.contract_type || '\u2014'}
                        </text>
                      )}
                    </>
                  ) : (
                    <>
                      {/* No-dates: dashed placeholder bar */}
                      <rect
                        x={LABEL_WIDTH + 4}
                        y={y + 10}
                        width={chartWidth - 8}
                        height={ROW_HEIGHT - 20}
                        fill="none"
                        stroke="rgba(255, 255, 255, 0.10)"
                        strokeWidth={1}
                        strokeDasharray="6 4"
                        rx={5}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => handleBarHover(e, contract)}
                        onMouseLeave={handleBarLeave}
                        onClick={() => onContractClick && onContractClick(contract.id)}
                      />
                      {/* "No dates" label */}
                      <text
                        x={LABEL_WIDTH + chartWidth / 2}
                        y={y + ROW_HEIGHT / 2 + 4}
                        textAnchor="middle"
                        fill="#9aa5b4"
                        fontSize={10}
                        fontStyle="italic"
                        fontFamily="'DM Sans', sans-serif"
                      >
                        No dates set
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </Box>

      {/* Hover Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleBarLeave}
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 320,
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 24px rgba(255, 255, 255, 0.10), 0 2px 8px rgba(255, 255, 255, 0.05)',
            borderRadius: '10px',
          },
        }}
      >
        {hoveredContract && (
          <TooltipContent contract={hoveredContract} />
        )}
      </Popover>
    </Paper>
  );
};

const TooltipContent = ({ contract }) => {
  const summary = contract.extraction_summary;
  const isPPA = contract.contract_type === 'PPA';

  const formatDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 0.5,
          lineHeight: 1.3,
          letterSpacing: '-0.01em',
        }}
      >
        {contract.original_filename}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.7rem',
          display: 'block',
          mb: 1.5,
        }}
      >
        {formatDate(contract.start_date)} &rarr; {formatDate(contract.end_date)}
      </Typography>

      <Chip
        label={contract.contract_type || 'Unknown'}
        size="small"
        sx={{
          mb: 1.5,
          bgcolor: isPPA ? 'rgba(217, 138, 43, 0.08)' : 'rgba(59, 130, 246, 0.08)',
          color: isPPA ? '#D98A2B' : '#2d7dd2',
          fontWeight: 600,
          fontSize: '0.68rem',
          height: 22,
          border: isPPA
            ? '1px solid rgba(217, 138, 43, 0.12)'
            : '1px solid rgba(59, 130, 246, 0.18)',
        }}
      />

      {summary && (
        <Box sx={{ mt: 0.5 }}>
          {isPPA ? (
            <PPATooltipFields summary={summary} />
          ) : (
            <GeneralTooltipFields summary={summary} />
          )}
        </Box>
      )}
    </Box>
  );
};

const TooltipRow = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.4, alignItems: 'baseline' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.66rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          minWidth: 70,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.primary',
          fontSize: '0.72rem',
          fontWeight: 500,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const PPATooltipFields = ({ summary }) => (
  <>
    <TooltipRow label="Buyer" value={summary.buyer} />
    <TooltipRow label="Seller" value={summary.seller} />
    <TooltipRow label="Project" value={summary.project_name} />
    <TooltipRow
      label="Capacity"
      value={summary.capacity_mw ? `${summary.capacity_mw} MW` : null}
    />
    <TooltipRow
      label="Price"
      value={
        summary.contract_price_per_mwh
          ? `${summary.price_currency || 'A$'}${summary.contract_price_per_mwh}/MWh`
          : null
      }
    />
    <TooltipRow label="Tech" value={summary.technology_type} />
    <TooltipRow
      label="Term"
      value={summary.term_years ? `${summary.term_years} years` : null}
    />
  </>
);

const GeneralTooltipFields = ({ summary }) => (
  <>
    <TooltipRow label="Title" value={summary.contract_title} />
    <TooltipRow label="Type" value={summary.contract_type_detail} />
    <TooltipRow
      label="Parties"
      value={
        Array.isArray(summary.parties)
          ? summary.parties.join(', ')
          : summary.parties
      }
    />
    <TooltipRow label="Value" value={summary.contract_value} />
    <TooltipRow label="Currency" value={summary.currency} />
    <TooltipRow label="Law" value={summary.governing_law} />
  </>
);

export default ContractTimeline;
